import { Row, Col, Typography, message, Form } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker/generatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import { Formik, useFormikContext } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type PeriodPayload } from '../../../../api/types';
import { useAccountContext } from '../../../../components/contexts/AccountContextManager';
import { DownloadFormItem } from '../../../../components/fields/DownloadModal/ui';
import { useThemeComponents } from '../../../../themes';
import { exportGiftVoucherRecords } from '../../../../utilities/export';
import useGiftVoucherDownloadModalOptions, { allOption } from './useGiftVoucherDownloadModalOptions';

type GiftVoucherDownloadState = {
    company: string;
    period: PeriodPayload;
};

export type GiftVoucherDownloadModalProps = {
    companyOptions: {
        label: string;
        value: string;
    }[];
    isDownloading: boolean;
    loading: boolean;
    open: boolean;
    onClose: () => void;
    forAdmin?: boolean;
};

const allowEmpty: [boolean, boolean] = [true, true];
const disabledDate: RangePickerProps<Dayjs>['disabledDate'] = value => {
    const invalidDate = dayjs().add(1, 'day');

    return dayjs(value).isSameOrAfter(invalidDate, 'day');
};

const GiftVoucherDownloadModal = ({
    companyOptions,
    isDownloading,
    loading,
    open,
    onClose,
    forAdmin,
}: GiftVoucherDownloadModalProps) => {
    const { t } = useTranslation(['giftVoucherList', 'ciModal']);
    const {
        FormFields: { SelectField, RangePickerField },
        Modal,
    } = useThemeComponents();

    const { values, resetForm } = useFormikContext<GiftVoucherDownloadState>();

    const handleClose = useCallback(() => {
        // Pre-select module if there is no other choice
        if (companyOptions.length !== 1) {
            resetForm();
        } else {
            resetForm({
                values: {
                    company: values.company,
                    period: initialValues.period,
                },
            });
        }
        onClose();
    }, [companyOptions.length, onClose, resetForm, values.company]);

    return (
        <Modal
            cancelButtonProps={{ disabled: isDownloading }}
            cancelText={t('ciModal:actions.cancel')}
            okButtonProps={{
                disabled: !values.company,
                form: 'giftVoucherDownloadModalForm',
                htmlType: 'submit',
                loading: isDownloading,
            }}
            okText={t('ciModal:actions.download')}
            onCancel={handleClose}
            open={open}
            title={t('giftVoucherList:downloadModal.title')}
            width={480}
            centered
        >
            <Row gutter={[14, 14]}>
                <Col span={24}>
                    <Typography>{t('giftVoucherList:downloadModal.description')}</Typography>
                </Col>
                <Col span={24}>
                    <DownloadFormItem $forAdmin={forAdmin}>
                        <SelectField
                            bordered={!forAdmin}
                            loading={loading}
                            name="company"
                            options={companyOptions}
                            placeholder={t('ciModal:options.placeHolders.selectCompany')}
                            showSearch
                        />
                    </DownloadFormItem>
                </Col>
                <Col span={24}>
                    <DownloadFormItem $forAdmin={forAdmin}>
                        <RangePickerField
                            allowEmpty={allowEmpty}
                            bordered={!forAdmin}
                            defaultValue={undefined}
                            disabledDate={disabledDate}
                            label=""
                            name="period"
                            picker="date"
                            showTime={false}
                        />
                    </DownloadFormItem>
                </Col>
            </Row>
        </Modal>
    );
};

export default GiftVoucherDownloadModal;

const initialValues: GiftVoucherDownloadState = {
    company: allOption.value,
    period: { start: undefined, end: undefined },
};

export const useGiftVoucherDownloadModal = () => {
    const { t } = useTranslation('ciModal');
    const [visible, setVisible] = useState(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const { token } = useAccountContext();

    const { loading, companyOptions, getSelectedCompanyIds, getSelectedCompanyName } =
        useGiftVoucherDownloadModalOptions();

    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    const onSubmit = useCallback(
        async (values, helpers) => {
            if (!values.company) {
                return;
            }

            try {
                setIsDownloading(true);

                await exportGiftVoucherRecords({
                    period: values.period,
                    companyIds: getSelectedCompanyIds(values.company),
                    companyName: getSelectedCompanyName(values.company),
                    token,
                });

                setIsDownloading(false);
            } catch (error) {
                message.error(t('ciModal:messages.noRecordToDownload'));
                setIsDownloading(false);
            } finally {
                // Pre-select module if there is no other choice
                if (companyOptions.length !== 1) {
                    helpers.resetForm();
                } else {
                    helpers.resetForm({
                        values: {
                            company: values.company,
                            period: initialValues.period,
                        },
                    });
                }
                actions.close();
            }
        },
        [actions, companyOptions.length, getSelectedCompanyIds, getSelectedCompanyName, t, token]
    );

    return {
        ...actions,
        render: (props: Pick<GiftVoucherDownloadModalProps, 'forAdmin'>) => (
            <Formik initialValues={initialValues} onSubmit={onSubmit}>
                {({ handleSubmit }) => (
                    <Form
                        id="giftVoucherDownloadModalForm"
                        name="giftVoucherDownloadModalForm"
                        onSubmitCapture={handleSubmit}
                    >
                        <GiftVoucherDownloadModal
                            companyOptions={companyOptions}
                            isDownloading={isDownloading}
                            loading={loading}
                            onClose={actions.close}
                            open={visible}
                            {...props}
                        />
                    </Form>
                )}
            </Formik>
        ),
    };
};
